import { useQuery } from '@tanstack/react-query';
import { useTypedOpenApi } from '../providers/useTypedOpenApi';
import { PLAN_SLUG } from './orgPlan';

export type ViewerPlan = {
  /** The viewer's organization is on a paid plan. */
  isSubscribed: boolean;
  /** …and is trialling it. */
  isTrialUser: boolean;
};

/** A plan that hasn't been heard about, or a signed-out viewer: free, and not trialling. */
const NO_PLAN: ViewerPlan = { isSubscribed: false, isTrialUser: false };

/**
 * Whether the signed-in viewer's organization pays for a plan, for the public sites that have no
 * synced database to read it from — what decides ads, the gold badge and the upgrade button.
 * `GET /organization/plan`, asked only while someone is signed in: this package can't read Clerk
 * itself, so the caller passes the Clerk user id. The cache is keyed by it, so the next person to
 * sign in on the same tab never gets the last one's plan.
 */
export const useViewerPlan = (userId: string | null | undefined): ViewerPlan => {
  const isSignedIn = Boolean(userId);
  const { apiClient } = useTypedOpenApi();

  const { data } = useQuery({
    queryKey: ['viewerPlan', userId],
    queryFn: async () => (await apiClient.get('/organization/plan')).data.plan,
    enabled: isSignedIn,
    staleTime: 5 * 60 * 1000,
  });

  if (!isSignedIn || !data) {
    return NO_PLAN;
  }

  return {
    isSubscribed: data.slug !== null && data.slug !== PLAN_SLUG.free,
    isTrialUser: data.status === 'trialing',
  };
};
